import { cn } from '@april9au/react-common/utils';

import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';

/**
 * The chat's progress indicator: which module this is, roughly how long is
 * left, and a checkpoint rail built from the topic's `section_divider`s — each
 * segment filling with the steps played inside it, so the rail measures
 * progress rather than just naming the checkpoint the learner is in.
 *
 * It is a node, not a bar. `AppHeader` is the one header in the app and this
 * goes in its `progress` slot, exactly as `OverallProgress` does on the
 * journey — so a module and the journey share one piece of chrome and differ
 * only in what they say about progress.
 */

export type CheckpointSegment = {
  /** 1-based. */
  index: number;
  title: string;
  state: 'done' | 'current' | 'todo';
  /** How much of this segment the learner has played, 0–1. */
  fill: number;
};

export type ModuleProgressProps = {
  /** 1-based position in the learner's journey. */
  moduleIndex: number;
  moduleTotal: number;
  title: string;
  checkpoints: Array<CheckpointSegment>;
  /** How far through the whole module the learner is, 0–1. */
  fraction: number;
  /** Rough minutes remaining, or `null` when the topic has no duration. */
  minutesLeft?: number | null;
  className?: string;
};

// Navy behind you, yellow where you are — ticket 01's roles. `todo` needs no
// entry: an unplayed segment is its own empty track.
const FILL: Record<CheckpointSegment['state'], string> = {
  done: 'bg-checkpoint-done',
  current: 'bg-checkpoint-current',
  todo: 'bg-checkpoint-current',
};

const LABEL: Record<CheckpointSegment['state'], string> = {
  done: 'text-chat-ink/60',
  current: 'text-chat-ink',
  todo: 'text-chat-ink-muted/70',
};

export function ModuleProgress({
  moduleIndex,
  moduleTotal,
  title,
  checkpoints,
  fraction,
  minutesLeft = null,
  className,
}: ModuleProgressProps) {
  const { chat } = useStrings();
  const current = checkpoints.find((checkpoint) => checkpoint.state === 'current')?.index ?? checkpoints.length;
  const percent = Math.round(fraction * 100);

  // A topic with no `section_divider` gets one checkpoint named after the
  // module, which then printed the module title twice, one line under itself.
  // A single segment needs no name — the module line above it is the name.
  const showLabels = checkpoints.length > 1;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-baseline justify-between gap-3">
        {/* `dir="auto"` throughout: until the `language` documents exist these
            are English fallbacks, and an LTR count inside an RTL paragraph
            renders as `of 4 2`. */}
        <p dir="auto" className="text-chat-ink min-w-0 truncate text-xs font-bold">
          {formatString(chat.chat_module_label, { current: moduleIndex, total: moduleTotal })}
          <span className="text-chat-ink/50"> · </span>
          <span className="font-semibold">{title}</span>
        </p>
        {minutesLeft === null ? null : (
          <p dir="auto" className="text-chat-ink-muted shrink-0 text-xs font-semibold tabular-nums">
            {formatString(chat.chat_minutes_left, { minutes: minutesLeft })}
          </p>
        )}
      </div>

      <ol
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${checkpoints.length}, minmax(0, 1fr))` }}
        aria-label={
          // "Checkpoint 1 of 1" says nothing. A module with no dividers is one
          // segment, and what it has to report is how far in the learner is.
          checkpoints.length > 1
            ? formatString(chat.chat_checkpoint_label, { current, total: checkpoints.length })
            : formatString(chat.chat_module_progress, { percent })
        }
      >
        {checkpoints.map((checkpoint) => (
          <li key={checkpoint.index} className="min-w-0">
            {/* A track with a fill that scales out of the reading edge, the
                same mechanism as onboarding's `StepCounter` — so a segment
                *travels* as the learner plays it rather than flipping colour
                the moment they arrive. */}
            <div className="bg-checkpoint-todo relative h-1.5 overflow-hidden rounded-full" aria-hidden>
              <span
                className={cn(
                  'absolute inset-0 origin-left rounded-full transition-transform duration-500 ease-out motion-reduce:transition-none rtl:origin-right',
                  FILL[checkpoint.state],
                )}
                style={{ transform: `scaleX(${checkpoint.fill})` }}
              />
            </div>
            {showLabels ? (
              <span dir="auto" className={cn('mt-1 block truncate text-[11px] font-semibold', LABEL[checkpoint.state])}>
                {checkpoint.title}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
